import React, { useState } from 'react'
import Chrome from '#src/components/Chrome'
import { CircleLoader, FullBlockLoader, GlobalLoader, Notification } from '@deal/components'
import SellableRouter from '#src/components/SellableRouter'
import CharterDetailsPage from './ExperienceDetailsPage/departments/yacht-charters'
import CharterBookingPage from './ExperienceBookingPage/departments/yacht-charters'
import {
  ExperienceByExpertCuratedOrderComponent,
  ExperienceByFriendlyIdOrIdComponent,
  SellableType,
  ExperienceDetailsFragment,
  ExpertCuratedOrderFragment
} from '#src/generated/graphql'
import Logger from '#src/services/logger'
import ChromeWithCategorySidebar from '../ChromeWithCategorySidebar'
import ChromeWithLeadSidebar from '../ChromeWithLeadSidebar'
import { useParams } from 'react-router-dom'
export interface ExperiencePageProps {
  sellable: ExperienceDetailsFragment
  onSetChromeFixed: (isChromeFixed: boolean) => void
}

export interface ExperienceBookingProps {
  sellable: ExperienceDetailsFragment
  expertCuratedOrder: ExpertCuratedOrderFragment
  onSetChromeFixed: (isChromeFixed: boolean) => void
}

const ExperiencesPage: React.SFC = () => {
  const { sellableIdOrFriendlyId: experienceId, expertCuratedOrderId } = useParams()
  const [isChromeFixed, setChromeFixed] = useState(false)

  if (experienceId) {
    return (
      <ExperienceByFriendlyIdOrIdComponent variables={{ friendlyIdOrId: experienceId }}>
        {({ data, loading }) => {
          if (data && data.findSellable) {
            const sellable = data.findSellable

            return (
              <ChromeWithCategorySidebar
                categoryId={data.findSellable.categories[0].id}
                fixed={isChromeFixed}
              >
                <SellableRouter requestedType={SellableType.SERVICE} sellable={sellable}>
                  <GlobalLoader loading={loading} />
                  <CharterDetailsPage
                    sellable={sellable}
                    onSetChromeFixed={isChromeFixed => setChromeFixed(isChromeFixed)}
                  />
                </SellableRouter>
              </ChromeWithCategorySidebar>
            )
          } else if (loading) {
            return (
              <Chrome sidebar={true} fixed={isChromeFixed}>
                <FullBlockLoader loader={CircleLoader} />
              </Chrome>
            )
          } else {
            return (
              <Chrome fixed={isChromeFixed}>
                <Notification type="error">
                  There was an error loading this product, please try again.
                </Notification>
              </Chrome>
            )
          }
        }}
      </ExperienceByFriendlyIdOrIdComponent>
    )
  } else if (expertCuratedOrderId) {
    return (
      <ExperienceByExpertCuratedOrderComponent
        variables={{
          id: expertCuratedOrderId
        }}
      >
        {({ data, loading }) => {
          if (data && data.expertCuratedOrder) {
            const maybeFlagshipLineItem = data.expertCuratedOrder.order.lineItems.filter(
              lineItem => lineItem.isGroup
            )

            const flagshipLineItem = maybeFlagshipLineItem[0]

            if (flagshipLineItem.source.__typename !== 'OrderLineItemSellableSource') {
              throw new Error(
                `Attempting curated experience review on un-grouped sellable [${expertCuratedOrderId}]`
              )
            }

            const sellable = flagshipLineItem.source.sellable

            return (
              <ChromeWithLeadSidebar leadId={data.expertCuratedOrder.lead.id} fixed={isChromeFixed}>
                <GlobalLoader loading={loading} />
                <CharterBookingPage
                  sellable={sellable}
                  expertCuratedOrder={data.expertCuratedOrder}
                  onSetChromeFixed={isChromeFixed => setChromeFixed(isChromeFixed)}
                />
              </ChromeWithLeadSidebar>
            )
          } else {
            return (
              <Chrome fixed={isChromeFixed}>
                <FullBlockLoader loader={CircleLoader} />
              </Chrome>
            )
          }
        }}
      </ExperienceByExpertCuratedOrderComponent>
    )
  }

  Logger.captureException(
    'Attempted to render Experience Page without experienceId or expertCuratedOrderId'
  )

  return null
}

export default ExperiencesPage
